import React, { useState } from 'react';
import { Menu, X, ArrowRight, CheckCircle, Award, Code, Leaf, Building2, Truck, Stethoscope, Cable, Calculator, Home as HomeIcon, Mail, Phone, MapPin, Linkedin, Facebook, Twitter, Star } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { Badge } from '../components/ui/badge';
import { companyInfo, products, services, portfolio, team, testimonials, stats } from '../mock';

const Home = () => {
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [formData, setFormData] = useState({ name: '', email: '', phone: '', message: '' });

  const productIcons = [
    <HomeIcon className="w-10 h-10" />,
    <Calculator className="w-10 h-10" />,
    <Truck className="w-10 h-10" />,
    <Building2 className="w-10 h-10" />,
    <Stethoscope className="w-10 h-10" />,
    <Cable className="w-10 h-10" />
  ];

  const handleSubmit = (e) => {
    e.preventDefault();
    alert('Thank you for your interest! We will contact you soon.');
    setFormData({ name: '', email: '', phone: '', message: '' });
  };

  const handleChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  const scrollToSection = (id) => {
    const element = document.getElementById(id);
    if (element) {
      element.scrollIntoView({ behavior: 'smooth' });
      setIsMenuOpen(false);
    }
  };

  return (
    <div className="min-h-screen bg-white">
      {/* Header */}
      <header className="fixed top-0 left-0 right-0 glass-effect border-b border-gray-200 z-50 animate-fade-in-down">
        <div className="container mx-auto px-4 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-3 hover-lift">
              <img 
                src="https://customer-assets.emergentagent.com/job_d49a6c07-af68-4a14-85ae-cc9a48ffe43c/artifacts/a4pcdnbd_webcare_Logo.jpg" 
                alt="WebCare IT Solution" 
                className="h-12 w-auto transition-transform duration-300 hover:scale-105"
              />
            </div>
            
            {/* Desktop Navigation */}
            <nav className="hidden md:flex items-center space-x-8">
              <button onClick={() => scrollToSection('home')} className="text-gray-700 hover:text-blue-600 font-medium relative group">
                Home
                <span className="absolute bottom-0 left-0 w-0 h-0.5 bg-blue-600 group-hover:w-full transition-all duration-300"></span>
              </button>
              <button onClick={() => scrollToSection('about')} className="text-gray-700 hover:text-blue-600 font-medium relative group">
                About
                <span className="absolute bottom-0 left-0 w-0 h-0.5 bg-blue-600 group-hover:w-full transition-all duration-300"></span>
              </button>
              <button onClick={() => scrollToSection('products')} className="text-gray-700 hover:text-blue-600 font-medium relative group">
                Products
                <span className="absolute bottom-0 left-0 w-0 h-0.5 bg-blue-600 group-hover:w-full transition-all duration-300"></span>
              </button>
              <button onClick={() => scrollToSection('services')} className="text-gray-700 hover:text-blue-600 font-medium relative group">
                Services
                <span className="absolute bottom-0 left-0 w-0 h-0.5 bg-blue-600 group-hover:w-full transition-all duration-300"></span>
              </button>
              <button onClick={() => scrollToSection('portfolio')} className="text-gray-700 hover:text-blue-600 font-medium relative group">
                Portfolio
                <span className="absolute bottom-0 left-0 w-0 h-0.5 bg-blue-600 group-hover:w-full transition-all duration-300"></span>
              </button>
              <button onClick={() => scrollToSection('testimonials')} className="text-gray-700 hover:text-blue-600 font-medium relative group">
                Testimonials
                <span className="absolute bottom-0 left-0 w-0 h-0.5 bg-blue-600 group-hover:w-full transition-all duration-300"></span>
              </button>
              <Button onClick={() => scrollToSection('contact')} className="bg-blue-600 hover:bg-blue-700 text-white shine-effect shadow-lg hover:shadow-xl hover:scale-105">
                Contact Us
              </Button>
            </nav>

            {/* Mobile Menu Button */}
            <button 
              className="md:hidden text-gray-700"
              onClick={() => setIsMenuOpen(!isMenuOpen)}
            >
              {isMenuOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
            </button>
          </div>

          {/* Mobile Navigation */}
          {isMenuOpen && (
            <nav className="md:hidden mt-4 pb-4 flex flex-col space-y-3">
              <button onClick={() => scrollToSection('home')} className="text-gray-700 hover:text-blue-600 transition-colors font-medium text-left">Home</button>
              <button onClick={() => scrollToSection('about')} className="text-gray-700 hover:text-blue-600 transition-colors font-medium text-left">About</button>
              <button onClick={() => scrollToSection('products')} className="text-gray-700 hover:text-blue-600 transition-colors font-medium text-left">Products</button>
              <button onClick={() => scrollToSection('services')} className="text-gray-700 hover:text-blue-600 transition-colors font-medium text-left">Services</button>
              <button onClick={() => scrollToSection('portfolio')} className="text-gray-700 hover:text-blue-600 transition-colors font-medium text-left">Portfolio</button>
              <button onClick={() => scrollToSection('testimonials')} className="text-gray-700 hover:text-blue-600 transition-colors font-medium text-left">Testimonials</button>
              <Button onClick={() => scrollToSection('contact')} className="bg-blue-600 hover:bg-blue-700 text-white w-full">Contact Us</Button>
            </nav>
          )}
        </div>
      </header>

      {/* Hero Section */}
      <section id="home" className="pt-32 pb-20 px-4 relative overflow-hidden">
        {/* Animated background elements */}
        <div className="absolute top-20 right-10 w-72 h-72 bg-blue-400/10 rounded-full blur-3xl floating"></div>
        <div className="absolute bottom-20 left-10 w-96 h-96 bg-orange-400/10 rounded-full blur-3xl floating" style={{animationDelay: '2s'}}></div>
        
        <div className="container mx-auto relative z-10">
          <div className="flex flex-col lg:flex-row items-center gap-12">
            <div className="flex-1 space-y-6">
              <Badge className="bg-green-100 text-green-700 hover:bg-green-200 border-green-300 animate-fade-in-up">
                <Leaf className="w-3 h-3 mr-1" />
                Sustainable Technology Solutions
              </Badge>
              <h1 className="text-5xl lg:text-6xl font-bold text-gray-900 leading-tight animate-fade-in-up delay-100">
                Innovative Software Solutions <span className="gradient-text">Since 2008</span>
              </h1>
              <p className="text-xl text-gray-600 leading-relaxed animate-fade-in-up delay-200">
                Transforming businesses with cutting-edge applications, portals, websites, and standalone software. 
                17+ years of excellence in delivering technology that grows with you.
              </p>
              <div className="flex flex-wrap gap-4 animate-fade-in-up delay-300">
                <Button onClick={() => scrollToSection('products')} className="bg-blue-600 hover:bg-blue-700 text-white text-lg px-8 py-6 shine-effect shadow-lg hover:shadow-2xl hover:-translate-y-1">
                  Explore Products <ArrowRight className="ml-2 w-5 h-5" />
                </Button>
                <Button onClick={() => scrollToSection('contact')} variant="outline" className="border-2 border-gray-300 text-gray-700 hover:bg-gray-50 text-lg px-8 py-6 hover:-translate-y-1 hover:shadow-lg">
                  Get in Touch
                </Button>
              </div>
            </div>
            <div className="flex-1 animate-fade-in-right delay-400">
              <div className="relative">
                <div className="absolute inset-0 bg-gradient-to-br from-blue-400/20 to-orange-400/20 rounded-3xl blur-3xl animate-pulse"></div>
                <div className="relative bg-white p-8 rounded-2xl shadow-2xl border border-gray-200 hover-lift">
                  <div className="grid grid-cols-2 gap-6">
                    {stats.map((stat, index) => (
                      <div key={index} className="text-center p-4 bg-gradient-to-br from-gray-50 to-white rounded-xl hover:shadow-lg transition-all duration-300 hover:-translate-y-1">
                        <div className="text-3xl font-bold text-blue-600">{stat.value}</div>
                        <div className="text-sm text-gray-600 mt-1">{stat.label}</div>
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* About Section */}
      <section id="about" className="py-20 px-4 bg-gradient-to-b from-gray-50 to-white relative overflow-hidden">
        <div className="absolute top-0 right-0 w-96 h-96 bg-green-400/5 rounded-full blur-3xl"></div>
        <div className="container mx-auto relative z-10">
          <div className="max-w-3xl mx-auto text-center mb-16 animate-fade-in-up">
            <h2 className="text-4xl font-bold text-gray-900 mb-6">About WebCare IT Solution</h2>
            <p className="text-lg text-gray-600 leading-relaxed">
              Since 2008, WebCare IT Solution has been at the forefront of software innovation, delivering 
              robust applications, dynamic portals, engaging websites, and powerful standalone software solutions. 
              We take pride in our 6 flagship products that serve diverse industries across India.
            </p>
          </div>
          <div className="grid md:grid-cols-3 gap-8">
            <Card className="border-2 hover:border-blue-400 hover-lift shine-effect animate-fade-in-up delay-100 group bg-white/80 backdrop-blur">
              <CardHeader>
                <div className="w-16 h-16 bg-gradient-to-br from-blue-100 to-blue-200 rounded-2xl flex items-center justify-center mb-4 group-hover:scale-110 transition-transform duration-300">
                  <Award className="w-12 h-12 text-blue-600" />
                </div>
                <CardTitle className="text-xl">17+ Years Excellence</CardTitle>
              </CardHeader>
              <CardContent>
                <p className="text-gray-600">Nearly two decades of delivering exceptional software solutions and building lasting client relationships.</p>
              </CardContent>
            </Card>
            <Card className="border-2 hover:border-orange-400 hover-lift shine-effect animate-fade-in-up delay-200 group bg-white/80 backdrop-blur">
              <CardHeader>
                <div className="w-16 h-16 bg-gradient-to-br from-orange-100 to-orange-200 rounded-2xl flex items-center justify-center mb-4 group-hover:scale-110 transition-transform duration-300">
                  <Code className="w-12 h-12 text-orange-600" />
                </div>
                <CardTitle className="text-xl">6 Flagship Products</CardTitle>
              </CardHeader>
              <CardContent>
                <p className="text-gray-600">Comprehensive suite of industry-specific software products designed for maximum efficiency and growth.</p>
              </CardContent>
            </Card>
            <Card className="border-2 hover:border-green-400 hover-lift shine-effect animate-fade-in-up delay-300 group bg-white/80 backdrop-blur">
              <CardHeader>
                <div className="w-16 h-16 bg-gradient-to-br from-green-100 to-green-200 rounded-2xl flex items-center justify-center mb-4 group-hover:scale-110 transition-transform duration-300">
                  <Leaf className="w-12 h-12 text-green-600" />
                </div>
                <CardTitle className="text-xl">Nature Conscious</CardTitle>
              </CardHeader>
              <CardContent>
                <p className="text-gray-600">Building sustainable digital solutions while promoting environmental awareness in technology.</p>
              </CardContent>
            </Card>
          </div>
        </div>
      </section>

      {/* Products Section */}
      <section id="products" className="py-20 px-4 relative overflow-hidden">
        <div className="absolute top-20 left-0 w-96 h-96 bg-blue-400/5 rounded-full blur-3xl"></div>
        <div className="container mx-auto relative z-10">
          <div className="text-center mb-16 animate-fade-in-up">
            <h2 className="text-4xl font-bold text-gray-900 mb-4">Our Software Products</h2>
            <p className="text-lg text-gray-600 max-w-2xl mx-auto">
              Industry-leading software solutions designed to streamline operations and drive business growth
            </p>
          </div>
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
            {products.map((product, index) => (
              <Card key={product.id} className={`group hover:shadow-2xl transition-all duration-500 border-2 hover:border-blue-400 hover-lift shine-effect animate-fade-in-up delay-${index * 100} bg-white/80 backdrop-blur`}>
                <CardHeader>
                  <div className="w-16 h-16 bg-gradient-to-br from-blue-100 to-blue-200 rounded-xl flex items-center justify-center text-blue-600 mb-4 group-hover:scale-125 group-hover:rotate-12 transition-all duration-300 shadow-lg">
                    {productIcons[index]}
                  </div>
                  <CardTitle className="text-xl group-hover:text-blue-600 transition-colors">{product.name}</CardTitle>
                  <CardDescription className="text-base">{product.description}</CardDescription>
                </CardHeader>
                <CardContent>
                  <div className="space-y-2">
                    {product.features.map((feature, idx) => (
                      <div key={idx} className="flex items-center text-sm text-gray-600 group-hover:translate-x-2 transition-transform duration-300" style={{transitionDelay: `${idx * 50}ms`}}>
                        <CheckCircle className="w-4 h-4 text-green-500 mr-2 flex-shrink-0" />
                        {feature}
                      </div>
                    ))}
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* Services Section */}
      <section id="services" className="py-20 px-4 bg-gray-50">
        <div className="container mx-auto">
          <div className="text-center mb-16">
            <h2 className="text-4xl font-bold text-gray-900 mb-4">Our Services</h2>
            <p className="text-lg text-gray-600 max-w-2xl mx-auto">
              Comprehensive software development services tailored to your business needs
            </p>
          </div>
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
            {services.map((service, index) => (
              <Card key={service.id} className={`border-l-4 border-l-blue-600 hover:shadow-2xl hover-lift shine-effect animate-fade-in-up delay-${index * 100} bg-white/80 backdrop-blur group transition-all duration-300`}>
                <CardHeader>
                  <CardTitle className="text-xl group-hover:text-blue-600 transition-colors">{service.title}</CardTitle>
                  <CardDescription className="text-base mt-2">{service.description}</CardDescription>
                </CardHeader>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* Portfolio Section */}
      <section id="portfolio" className="py-20 px-4">
        <div className="container mx-auto">
          <div className="text-center mb-16">
            <h2 className="text-4xl font-bold text-gray-900 mb-4">Success Stories</h2>
            <p className="text-lg text-gray-600 max-w-2xl mx-auto">
              Real-world implementations that showcase our expertise and commitment to excellence
            </p>
          </div>
          <div className="grid md:grid-cols-2 gap-8">
            {portfolio.map((project) => (
              <Card key={project.id} className="hover:shadow-xl transition-shadow border-2">
                <CardHeader>
                  <div className="flex justify-between items-start mb-2">
                    <CardTitle className="text-xl">{project.title}</CardTitle>
                    <Badge variant="outline" className="text-blue-600 border-blue-600">{project.year}</Badge>
                  </div>
                  <p className="text-sm text-gray-500 mb-3">{project.client}</p>
                  <CardDescription className="text-base">{project.description}</CardDescription>
                </CardHeader>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* Team Section */}
      <section id="team" className="py-20 px-4 bg-gray-50">
        <div className="container mx-auto">
          <div className="text-center mb-16">
            <h2 className="text-4xl font-bold text-gray-900 mb-4">Our Team</h2>
            <p className="text-lg text-gray-600 max-w-2xl mx-auto">
              Meet the passionate professionals driving innovation at WebCare IT Solution
            </p>
          </div>
          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-8">
            {team.map((member) => (
              <Card key={member.id} className="text-center hover:shadow-lg transition-shadow">
                <CardHeader>
                  <div className="w-24 h-24 bg-gradient-to-br from-blue-400 to-orange-400 rounded-full mx-auto mb-4 flex items-center justify-center text-white text-3xl font-bold">
                    {member.name.split(' ').map(n => n[0]).join('')}
                  </div>
                  <CardTitle className="text-lg">{member.name}</CardTitle>
                  <p className="text-sm text-blue-600 font-medium">{member.role}</p>
                </CardHeader>
                <CardContent>
                  <p className="text-sm text-gray-600">{member.bio}</p>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* Testimonials Section */}
      <section id="testimonials" className="py-20 px-4 relative overflow-hidden">
        <div className="absolute top-20 right-0 w-96 h-96 bg-yellow-400/5 rounded-full blur-3xl"></div>
        <div className="container mx-auto relative z-10">
          <div className="text-center mb-16 animate-fade-in-up">
            <h2 className="text-4xl font-bold text-gray-900 mb-4">What Our Clients Say</h2>
            <p className="text-lg text-gray-600 max-w-2xl mx-auto">
              Don't just take our word for it - hear from our satisfied clients
            </p>
          </div>
          <div className="grid md:grid-cols-2 gap-8">
            {testimonials.map((testimonial, index) => (
              <Card key={testimonial.id} className={`border-2 hover:border-blue-400 hover-lift shine-effect animate-fade-in-up delay-${index * 100} bg-white/80 backdrop-blur group`}>
                <CardHeader>
                  <div className="flex items-center gap-1 mb-3">
                    {[...Array(testimonial.rating)].map((_, i) => (
                      <Star key={i} className="w-5 h-5 fill-yellow-400 text-yellow-400 group-hover:scale-125 transition-transform" style={{transitionDelay: `${i * 50}ms`}} />
                    ))}
                  </div>
                  <p className="text-gray-700 italic mb-4 text-lg leading-relaxed">"{testimonial.feedback}"</p>
                  <div className="flex items-center gap-3">
                    <div className="w-12 h-12 bg-gradient-to-br from-blue-400 to-blue-600 rounded-full flex items-center justify-center text-white font-bold shadow-lg">
                      {testimonial.name.split(' ').map(n => n[0]).join('')}
                    </div>
                    <div>
                      <p className="font-semibold text-gray-900">{testimonial.name}</p>
                      <p className="text-sm text-gray-500">{testimonial.company}</p>
                    </div>
                  </div>
                </CardHeader>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* Contact Section */}
      <section id="contact" className="py-20 px-4 bg-gradient-to-b from-gray-50 to-white relative overflow-hidden">
        <div className="absolute top-0 left-0 w-96 h-96 bg-blue-400/5 rounded-full blur-3xl"></div>
        <div className="container mx-auto relative z-10">
          <div className="text-center mb-16 animate-fade-in-up">
            <h2 className="text-4xl font-bold text-gray-900 mb-4">Get in Touch</h2>
            <p className="text-lg text-gray-600 max-w-2xl mx-auto">
              Have a project in mind? Let's discuss how we can help transform your business
            </p>
          </div>
          <div className="grid lg:grid-cols-2 gap-12 max-w-6xl mx-auto">
            <div className="space-y-8 animate-fade-in-left">
              <div>
                <h3 className="text-2xl font-bold text-gray-900 mb-6">Contact Information</h3>
                <div className="space-y-4">
                  <div className="flex items-start gap-4 group hover-lift p-4 rounded-xl transition-all duration-300 hover:bg-white hover:shadow-lg">
                    <div className="w-12 h-12 bg-gradient-to-br from-blue-100 to-blue-200 rounded-lg flex items-center justify-center flex-shrink-0 group-hover:scale-110 transition-transform shadow-lg">
                      <Mail className="w-6 h-6 text-blue-600" />
                    </div>
                    <div>
                      <p className="font-semibold text-gray-900">Email</p>
                      <p className="text-gray-600">info@webcareitsolution.in</p>
                    </div>
                  </div>
                  <div className="flex items-start gap-4 group hover-lift p-4 rounded-xl transition-all duration-300 hover:bg-white hover:shadow-lg">
                    <div className="w-12 h-12 bg-gradient-to-br from-blue-100 to-blue-200 rounded-lg flex items-center justify-center flex-shrink-0 group-hover:scale-110 transition-transform shadow-lg">
                      <Phone className="w-6 h-6 text-blue-600" />
                    </div>
                    <div>
                      <p className="font-semibold text-gray-900">Phone</p>
                      <p className="text-gray-600">+91 97653 65311</p>
                    </div>
                  </div>
                  <div className="flex items-start gap-4 group hover-lift p-4 rounded-xl transition-all duration-300 hover:bg-white hover:shadow-lg">
                    <div className="w-12 h-12 bg-gradient-to-br from-blue-100 to-blue-200 rounded-lg flex items-center justify-center flex-shrink-0 group-hover:scale-110 transition-transform shadow-lg">
                      <MapPin className="w-6 h-6 text-blue-600" />
                    </div>
                    <div>
                      <p className="font-semibold text-gray-900">Location</p>
                      <p className="text-gray-600">India</p>
                    </div>
                  </div>
                </div>
              </div>
              <div>
                <h4 className="text-lg font-semibold text-gray-900 mb-4">Follow Us</h4>
                <div className="flex gap-4">
                  <button className="w-12 h-12 bg-gradient-to-br from-blue-600 to-blue-700 rounded-lg flex items-center justify-center text-white hover:scale-110 hover:shadow-xl transition-all duration-300 shine-effect">
                    <Linkedin className="w-5 h-5" />
                  </button>
                  <button className="w-12 h-12 bg-gradient-to-br from-blue-600 to-blue-700 rounded-lg flex items-center justify-center text-white hover:scale-110 hover:shadow-xl transition-all duration-300 shine-effect">
                    <Facebook className="w-5 h-5" />
                  </button>
                  <button className="w-12 h-12 bg-gradient-to-br from-blue-600 to-blue-700 rounded-lg flex items-center justify-center text-white hover:scale-110 hover:shadow-xl transition-all duration-300 shine-effect">
                    <Twitter className="w-5 h-5" />
                  </button>
                </div>
              </div>
            </div>
            <Card className="shadow-2xl hover-lift animate-fade-in-right border-2 border-gray-200 hover:border-blue-400 transition-all duration-300">
              <CardHeader>
                <CardTitle className="text-2xl">Send us a Message</CardTitle>
              </CardHeader>
              <CardContent>
                <form onSubmit={handleSubmit} className="space-y-4">
                  <div className="group">
                    <label className="block text-sm font-medium text-gray-700 mb-2">Name</label>
                    <Input 
                      name="name"
                      value={formData.name}
                      onChange={handleChange}
                      placeholder="Your name" 
                      required
                      className="w-full transition-all duration-300 focus:scale-105"
                    />
                  </div>
                  <div className="group">
                    <label className="block text-sm font-medium text-gray-700 mb-2">Email</label>
                    <Input 
                      name="email"
                      type="email"
                      value={formData.email}
                      onChange={handleChange}
                      placeholder="your@email.com" 
                      required
                      className="w-full transition-all duration-300 focus:scale-105"
                    />
                  </div>
                  <div className="group">
                    <label className="block text-sm font-medium text-gray-700 mb-2">Phone</label>
                    <Input 
                      name="phone"
                      value={formData.phone}
                      onChange={handleChange}
                      placeholder="+91 XXXXX XXXXX" 
                      required
                      className="w-full transition-all duration-300 focus:scale-105"
                    />
                  </div>
                  <div className="group">
                    <label className="block text-sm font-medium text-gray-700 mb-2">Message</label>
                    <Textarea 
                      name="message"
                      value={formData.message}
                      onChange={handleChange}
                      placeholder="Tell us about your project..." 
                      rows={4}
                      required
                      className="w-full transition-all duration-300 focus:scale-105"
                    />
                  </div>
                  <Button type="submit" className="w-full bg-gradient-to-r from-blue-600 to-blue-700 hover:from-blue-700 hover:to-blue-800 text-white shine-effect shadow-lg hover:shadow-2xl hover:scale-105">
                    Send Message
                  </Button>
                </form>
              </CardContent>
            </Card>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="bg-gray-900 text-white py-12 px-4">
        <div className="container mx-auto">
          <div className="grid md:grid-cols-3 gap-8 mb-8">
            <div>
              <img 
                src="https://customer-assets.emergentagent.com/job_d49a6c07-af68-4a14-85ae-cc9a48ffe43c/artifacts/a4pcdnbd_webcare_Logo.jpg" 
                alt="WebCare IT Solution" 
                className="h-12 w-auto mb-4 bg-white p-2 rounded"
              />
              <p className="text-gray-400 text-sm">
                {companyInfo.tagline}
              </p>
            </div>
            <div>
              <h4 className="font-semibold text-lg mb-4">Quick Links</h4>
              <div className="space-y-2 text-sm">
                <button onClick={() => scrollToSection('about')} className="block text-gray-400 hover:text-white transition-colors">About Us</button>
                <button onClick={() => scrollToSection('products')} className="block text-gray-400 hover:text-white transition-colors">Products</button>
                <button onClick={() => scrollToSection('services')} className="block text-gray-400 hover:text-white transition-colors">Services</button>
                <button onClick={() => scrollToSection('contact')} className="block text-gray-400 hover:text-white transition-colors">Contact</button>
              </div>
            </div>
            <div>
              <h4 className="font-semibold text-lg mb-4 flex items-center gap-2">
                <Leaf className="w-5 h-5 text-green-400" />
                Our Commitment
              </h4>
              <p className="text-gray-400 text-sm">
                {companyInfo.natureMessage}
              </p>
            </div>
          </div>
          <div className="border-t border-gray-800 pt-8 text-center text-sm text-gray-400">
            <p>&copy; {new Date().getFullYear()} WebCare IT Solution. All rights reserved. | Established {companyInfo.established}</p>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default Home;
